import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { IMG } from "../lib/content";
import { FadeUp, PageHero } from "../components/Reveal";

const METALS = ["Silver", "9ct Yellow Gold", "9ct White Gold", "9ct Rose Gold", "18ct Gold", "Platinum", "Not sure yet"];
const BUDGETS = ["Under £250", "£250 – £500", "£500 – £1,000", "£1,000 – £2,500", "£2,500+", "Prefer to discuss"];

const field = "w-full bg-ink-2 border border-white/10 focus:border-gold outline-none px-4 py-3.5 text-sm text-ivory placeholder:text-stone-mist/50 transition-colors";

export default function Bespoke() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", idea: "", metal: "", budget: "", description: "", message: "" });
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFiles = (e) => {
    const list = Array.from(e.target.files || []).slice(0, 3);
    Promise.all(
      list.map(
        (f) =>
          new Promise((resolve) => {
            if (f.size > 1024 * 1024) return resolve({ name: f.name, tooLarge: true });
            const reader = new FileReader();
            reader.onload = () => resolve({ name: f.name, data: reader.result });
            reader.readAsDataURL(f);
          })
      )
    ).then(setFiles);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", {
        type: "bespoke",
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: "Bespoke jewellery enquiry",
        message: form.message || form.description,
        meta: {
          idea: form.idea,
          preferred_metal: form.metal,
          budget: form.budget,
          description: form.description,
          inspiration: files.map((f) => (f.tooLarge ? `${f.name} (too large — please email)` : f.name)),
          inspiration_images: files.filter((f) => !f.tooLarge).map((f) => f.data),
        },
      });
      setSent(true);
      toast.success("Bespoke enquiry sent. We'll be in touch.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="bespoke-page">
      <PageHero
        eyebrow="Bespoke Jewellery"
        title="Made for You"
        sub="Discuss unique jewellery ideas and commission bespoke pieces, crafted with you at every stage in our Isle of Man workshop."
        image={IMG.carving}
      />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 grid grid-cols-1 lg:grid-cols-2 gap-14">
        <FadeUp>
          <p className="text-stone-mist leading-relaxed font-light mb-5">
            Celtic Gold's fully equipped workshop allows us to create one-of-a-kind jewellery. We work closely with
            you throughout the bespoke process, taking your preferences and wishes into consideration and keeping
            you updated at each stage.
          </p>
          <p className="text-stone-mist leading-relaxed font-light mb-5">
            The majority of the craftsmanship is completed in-house on the Isle of Man. Bespoke commissions depend
            on jeweller availability and the design required — we will always discuss timescales with you honestly
            before any work begins.
          </p>
          <div className="grid grid-cols-2 gap-4 mt-8">
            <img src={IMG.workshop} alt="Workshop craftsmanship" className="w-full aspect-square object-cover border border-white/5" />
            <img src={IMG.setting} alt="Stone setting" className="w-full aspect-square object-cover border border-white/5 mt-8" />
          </div>
        </FadeUp>

        <FadeUp delay={0.1}>
          {sent ? (
            <div className="border border-gold/30 bg-gold/5 p-10 text-center" data-testid="bespoke-success">
              <h2 className="font-serif text-3xl text-ivory font-light mb-4">Thank you</h2>
              <p className="text-stone-mist font-light">
                Your bespoke enquiry has been sent. A member of the team will be in touch to discuss your idea.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-white/10 bg-ink-2/50 p-7 space-y-4" data-testid="bespoke-form">
              <h2 className="font-serif text-2xl text-ivory font-light mb-2">Start a Bespoke Enquiry</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input required value={form.name} onChange={set("name")} placeholder="Name *" className={field} data-testid="bespoke-name" />
                <input required type="email" value={form.email} onChange={set("email")} placeholder="Email *" className={field} data-testid="bespoke-email" />
                <input value={form.phone} onChange={set("phone")} placeholder="Telephone" className={field} data-testid="bespoke-phone" />
                <input value={form.idea} onChange={set("idea")} placeholder="What would you like created?" className={field} data-testid="bespoke-idea" />
                <select value={form.metal} onChange={set("metal")} className={field} data-testid="bespoke-metal">
                  <option value="">Preferred metal</option>
                  {METALS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <select value={form.budget} onChange={set("budget")} className={field} data-testid="bespoke-budget">
                  <option value="">Approximate budget</option>
                  {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <textarea value={form.description} onChange={set("description")} placeholder="Describe your idea" rows={4} className={field} data-testid="bespoke-description" />
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-stone-mist mb-2">Inspiration images (optional, up to 3)</label>
                <input type="file" accept="image/*" multiple onChange={onFiles} data-testid="bespoke-files" className="text-sm text-stone-mist file:bg-ink-2 file:border file:border-gold/30 file:text-gold file:px-4 file:py-2 file:text-xs file:uppercase file:tracking-[0.2em] file:mr-4" />
                <p className="text-[11px] text-stone-mist mt-2">Larger images can be emailed to sales@celtic-gold.co.uk after submitting.</p>
              </div>
              <textarea value={form.message} onChange={set("message")} placeholder="Anything else we should know?" rows={3} className={field} data-testid="bespoke-message" />
              <button type="submit" disabled={sending} data-testid="bespoke-submit" className="w-full bg-gold text-ink py-4 text-xs uppercase tracking-[0.25em] font-medium hover:bg-gold-glow transition-colors duration-300 disabled:opacity-50">
                {sending ? "Sending..." : "Send Bespoke Enquiry"}
              </button>
            </form>
          )}
        </FadeUp>
      </section>
    </main>
  );
}
